// KAIZEN_CONSOLE_ACTIVITY_STRIP_V3

import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  AnimatePresence,
  motion,
} from 'motion/react';

import { useRealtime } from '../../contexts/RealtimeContext';

import {
  kaizenMicroTransition,
  kaizenStateTransition,
} from '../../lib/kaizenMotion';


function parseTime(
  value: unknown,
): number | null {

  if (
    typeof value !== 'string' ||
    !value
  ) {
    return null;
  }

  const parsed =
    Date.parse(value);

  return Number.isFinite(parsed)
    ? parsed
    : null;
}


function formatElapsed(
  milliseconds: number,
): string {

  const totalSeconds =
    Math.max(
      0,
      Math.floor(milliseconds / 1000),
    );

  const hours =
    Math.floor(totalSeconds / 3600);

  const minutes =
    Math.floor(
      (totalSeconds % 3600) / 60,
    );

  const seconds =
    totalSeconds % 60;


  if (hours > 0) {
    return (
      `${hours}:` +
      `${String(minutes).padStart(2, '0')}:` +
      `${String(seconds).padStart(2, '0')}`
    );
  }


  return (
    `${String(minutes).padStart(2, '0')}:` +
    `${String(seconds).padStart(2, '0')}`
  );
}


function formatAge(
  timestamp: number | null,
  now: number,
): string | null {

  if (timestamp === null) {
    return null;
  }

  const seconds =
    Math.max(
      0,
      Math.floor(
        (now - timestamp) / 1000,
      ),
    );


  if (seconds < 3) {
    return 'just now';
  }


  if (seconds < 60) {
    return `${seconds}s ago`;
  }


  const minutes =
    Math.floor(seconds / 60);


  if (minutes < 60) {
    return (
      `${minutes}m ` +
      `${seconds % 60}s ago`
    );
  }


  const hours =
    Math.floor(minutes / 60);


  return (
    `${hours}h ` +
    `${minutes % 60}m ago`
  );
}


function getStatusCopy(
  status: string,
): string {

  switch (status) {
    case 'queued':
      return 'Waiting for agent';

    case 'running':
      return 'Process active';

    case 'succeeded':
      return 'Completed successfully';

    case 'failed':
      return 'Execution failed';

    case 'cancelled':
      return 'Execution cancelled';

    case 'timed_out':
      return 'Execution timed out';

    default:
      return 'Job activity';
  }
}


function getTone(
  status: string,
): string {

  switch (status) {
    case 'queued':
      return [
        'border-sky-400/25',
        'bg-sky-400/10',
        'text-sky-300',
      ].join(' ');

    case 'running':
      return [
        'border-amber-400/25',
        'bg-amber-400/10',
        'text-amber-300',
      ].join(' ');

    case 'succeeded':
      return [
        'border-emerald-400/25',
        'bg-emerald-400/10',
        'text-emerald-300',
      ].join(' ');

    case 'failed':
    case 'timed_out':
      return [
        'border-rose-400/25',
        'bg-rose-400/10',
        'text-rose-300',
      ].join(' ');

    case 'cancelled':
      return [
        'border-slate-600',
        'bg-slate-800/60',
        'text-slate-300',
      ].join(' ');

    default:
      return [
        'border-slate-700',
        'bg-slate-900/70',
        'text-slate-400',
      ].join(' ');
  }
}


export function JobActivityStrip() {

  const realtime =
    useRealtime() as any;

  const job =
    realtime.activeJob as any;

  const logs =
    (
      Array.isArray(realtime.activeJobLogs)
        ? realtime.activeJobLogs
        : []
    ) as any[];


  const [now, setNow] =
    useState<number>(
      () => Date.now(),
    );


  const status =
    String(
      job?.status ?? '',
    ).toLowerCase();


  const live =
    status === 'queued' ||
    status === 'running';


  useEffect(() => {

    if (!live) {
      return;
    }


    setNow(Date.now());


    const timer =
      window.setInterval(
        () => {
          setNow(Date.now());
        },
        1000,
      );


    return () => {
      window.clearInterval(timer);
    };

  }, [
    live,
    job?.id,
  ]);


  const lastOutputTime =
    useMemo(() => {

      for (
        let index =
          logs.length - 1;

        index >= 0;

        index -= 1
      ) {

        const log =
          logs[index];


        const stream =
          String(
            log?.stream ??
            log?.type ??
            '',
          ).toLowerCase();


        const content =
          String(
            log?.data ??
            log?.message ??
            log?.content ??
            '',
          );


        if (
          stream !== 'system' &&
          content.trim().length > 0
        ) {

          return parseTime(
            log?.timestamp ??
            log?.createdAt ??
            log?.created_at,
          );
        }
      }


      return null;

    }, [
      logs,
    ]);





  const startedAt =
    parseTime(
      job?.startedAt ??
      job?.started_at ??
      job?.createdAt ??
      job?.created_at,
    );


  const finishedAt =
    parseTime(
      job?.finishedAt ??
      job?.finished_at ??
      job?.completedAt ??
      job?.completed_at,
    );


  const elapsed =
    startedAt !== null
      ? Math.max(
          0,
          (finishedAt ?? now) -
            startedAt,
        )
      : 0;


  const outputAge =
    formatAge(
      lastOutputTime,
      now,
    );


  const statusLabel =
    (
      status || 'idle'
    )
      .replace(/_/g, ' ')
      .toUpperCase();


  const jobId =
    typeof job?.id === 'string'
      ? job.id
      : '';


  return (
    <AnimatePresence initial={false}>

      {job && (

        <motion.div
          key={
            jobId ||
            'active-job'
          }
          aria-live="polite"
          initial={{
            opacity: 0,
            y: -6
          }}
          animate={{
            opacity: 1,
            y: 0
          }}
          exit={{
            opacity: 0,
            y: -4
          }}
          transition={
            kaizenStateTransition
          }
          className="
        mb-2
        flex
        min-h-[42px]
        flex-col
        gap-2
        border
        border-white/10
        bg-black/20
        px-3
        py-2
        font-mono
        text-[11px]
        backdrop-blur-sm
        sm:flex-row
        sm:items-center
        sm:justify-between
      "
    >

      <div
        className="
          flex
          min-w-0
          items-center
          gap-2.5
        "
      >

        <AnimatePresence
          mode="wait"
          initial={false}
        >
          <motion.span
            key={`job-status-${status}`}
            initial={{
              opacity: 0,
              scale: 0.94
            }}
            animate={{
              opacity: 1,
              scale: 1
            }}
            exit={{
              opacity: 0,
              scale: 0.96
            }}
            transition={
              kaizenMicroTransition
            }
            className={[
              'inline-flex',
              'shrink-0',
              'items-center',
              'gap-1.5',
              'border',
              'px-2',
              'py-1',
              'font-semibold',
              'tracking-[0.08em]',
              getTone(status),
            ].join(' ')}
          >

            <span
              className={[
                'h-1.5',
                'w-1.5',
                'rounded-full',
                'bg-current',
                live
                  ? 'animate-pulse'
                  : '',
              ].join(' ')}
            />

            {statusLabel}

          </motion.span>
        </AnimatePresence>


        <AnimatePresence
          mode="wait"
          initial={false}
        >
          <motion.span
            key={`job-copy-${status}`}
            initial={{
              opacity: 0,
              x: -3
            }}
            animate={{
              opacity: 1,
              x: 0
            }}
            exit={{
              opacity: 0,
              x: 3
            }}
            transition={
              kaizenStateTransition
            }
            className="
              truncate
              text-slate-400
            "
          >
            {getStatusCopy(status)}
          </motion.span>
        </AnimatePresence>

      </div>


      <div
        className="
          flex
          flex-wrap
          items-center
          gap-x-4
          gap-y-1
          text-slate-500
          sm:justify-end
        "
      >

        <span>
          Elapsed{' '}

          <strong
            className="
              font-medium
              text-slate-200
            "
          >
            {formatElapsed(elapsed)}
          </strong>
        </span>


        <span>
          Output{' '}

          <strong
            className={
              outputAge
                ? 'font-medium text-slate-300'
                : live
                  ? 'font-medium text-amber-300'
                  : 'font-medium text-slate-500'
            }
          >
            {
              outputAge ??
              (
                live
                  ? 'waiting for first output'
                  : 'no stdout received'
              )
            }
          </strong>
        </span>


        {
          jobId && (
            <span
              className="
                hidden
                text-slate-600
                lg:inline
              "
            >
              {jobId.slice(0, 8)}
            </span>
          )
        }

      </div>

        </motion.div>

      )}

    </AnimatePresence>
  );
}
